import axios from 'axios';
import { newEvent } from './helpers/newEvent.ts';

/**
 * @typedef {Object} Config
 * @property {Number} boxId
 * @property {Number} [page]
 * @property {String} [parameter]
 * @property {String} [loading]
 * @property {Number} [quantity]
 * @property {String} [insertPlace='before']
 * @property {(elements: Array<HTMLElement>) => Array<HTMLElement>} [transform]
 */

/** @type {Config} */
const DEFAULT_CONFIG = {
  boxId: null,
  page: 0,
  parameter: 'page',
  loading: 'Načítám...',
  quantity: 10,
  transform: null,
  insertPlace: 'before',
};

const EVENT_BEFORE_LOAD = 'load-more--before-load';
const EVENT_AFTER_LOAD = 'load-more--after-load';
const EVENT_AFTER_INSERT = 'load-more--after-insert';
const EVENT_ERROR = 'load-more--error';

export class LoadMore {
  /**
   * @param {HTMLElement} element
   * @param {HTMLElement} target
   * @param {Config} config
   */
  constructor(element, target, config) {
    this._element = element;
    this._target = target;
    this._config = this._getConfig(config);
    this._page = this._config.page;

    if (this._config.boxId === null || !this._target) {
      throw Error('Error with "boxId" or target element.');
    }

    this._elementContent = this._element.innerHTML;
    this._isFetiching = false;

    this._insert = {
      before: (elements) => {
        elements.forEach((el) => {
          this._target.parentElement.insertBefore(el, this._target);
        });
      },
      after: (elements) => {
        elements.forEach((el) => {
          this._target.insertAdjacentElement('afterend', el);
        });
      },
      inside: (elements) => {
        elements.forEach((el) => this._target.appendChild(el));
      },
    };

    this._addListeners();
  }

  /**
   * @description Returns default confiuration
   * @returns {Object}
   */
  static get defaultConfig() {
    return DEFAULT_CONFIG;
  }

  // Private

  /**
   * @description Returns current configutarion.
   * @param {Config} config
   * @returns {Config}
   */
  _getConfig(config) {
    return { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * @description The method is run before the server is called.
   */
  _beforeLoad() {
    this._element.innerHTML = this._config.loading;
    this._isFetiching = true;
    this._element.dispatchEvent(
      newEvent(EVENT_BEFORE_LOAD, { detail: { pageNumber: this._page } })
    );
  }

  /**
   * @description The method is run after the server call..
   */
  _afterLoad() {
    this._element.innerHTML = this._elementContent;
    this._page += 1;
    this._isFetiching = false;
    this._element.dispatchEvent(newEvent(EVENT_AFTER_LOAD));
  }

  /**
   * @description The method is run after html code is inserted to DOM
   */
  _afterInsert() {
    this._element.dispatchEvent(newEvent(EVENT_AFTER_INSERT));
  }

  /**
   * @description The method calls data from the server and returns an array of elements or empty array.
   * @returns {Promise<Array<HTMLElement>>}
   */
  async _loadData() {
    const { boxId, parameter } = this._config;
    const { articleIdsInStringToExclude } = window.articleIdsInStringToExclude || '';
    try {
      const response = await axios(
        `?_sobfr=${boxId}&${parameter}=${this._page}&ajaxload&exclude_articles=${articleIdsInStringToExclude}`
      );
      const parser = new DOMParser();

      const elements = [...parser.parseFromString(response.data, 'text/html').body.children];
      return elements;
    } catch (error) {
      this._element.dispatchEvent(newEvent(EVENT_ERROR));
      return [];
    }
  }

  /**
   * @description Inserts elements before/after/inside a defined target
   * @param {Array<HTMLElement>} elements
   */
  _insertElements(elements) {
    const { insertPlace } = this._config;

    this._insert[insertPlace](elements);
  }

  /**
   * @description If transform function exists, applies it on elements
   * @param {Array<HTMLElement>} elements
   * @returns {Array<HTMLElement>}
   */
  _transformElements(elements) {
    const { transform } = this._config;

    return typeof transform === 'function' ? transform(elements) : elements;
  }

  /**
   * @description Click callback.
   */
  async _clickHandler(event) {
    event.preventDefault();

    this._beforeLoad();
    const elements = await this._loadData();
    this._afterLoad();

    const transformedElems = this._transformElements(elements);
    this._insertElements(transformedElems);

    this._afterInsert();
  }

  _addListeners() {
    this._element.addEventListener('click', this._clickHandler.bind(this));
  }
}
